import { useRef, type KeyboardEvent } from "react";
import { Flex, Popover, Typography, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import {
  Button,
  STANDOUT_NOTATION_ICON,
  type StandoutNotation,
} from "@libertify/ui";
import { KEY, NOTATION_PILLS } from "./constants";
import { focusFirstOption } from "./helpers";

export type StandoutCreatePopoverProps = {
  /** The header: what picking a marker will do. */
  title: string;
  /** Viewport position under the selected text. */
  anchor: { top: number; left: number };
  onPick: (notation: StandoutNotation) => void;
  /** Escape: drop the selection. */
  onDismiss: () => void;
  /** A click outside: close, leaving the browser selection alone. */
  onClose: () => void;
};

/** The marker choices for a text selection, under a header saying what they do. */
export const StandoutCreatePopover = ({
  title,
  anchor,
  onPick,
  onDismiss,
  onClose,
}: StandoutCreatePopoverProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const menu = useRef<HTMLDivElement>(null);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    // Kept from the window listener that would also end a reselect.
    event.stopPropagation();
    onDismiss();
  };

  return (
    <Popover
      open
      // Only the click trigger makes AntD report clicks outside the popover.
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      // Once shown: it mounts hidden, and a hidden button can't take focus.
      afterOpenChange={(open) => {
        if (open) focusFirstOption(menu.current);
      }}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      content={
        <Flex
          ref={menu}
          vertical
          gap={4}
          role="menu"
          aria-label={title}
          onKeyDown={onKeyDown}
        >
          <Typography.Text
            type="secondary"
            strong
            style={{
              fontSize: token.fontSizeSM,
              textTransform: "uppercase",
              padding: `${token.paddingXXS}px ${token.paddingXS}px`,
            }}
          >
            {title}
          </Typography.Text>
          {NOTATION_PILLS.map((notation) => (
            <Button
              key={notation}
              role="menuitem"
              variant="tertiary"
              leftIcon={STANDOUT_NOTATION_ICON[notation]}
              style={{ justifyContent: "flex-start" }}
              onClick={() => onPick(notation)}
            >
              {t(`${KEY}.create.${notation}`)}
            </Button>
          ))}
        </Flex>
      }
    >
      {/* 1px point the popover hangs from. AntD never positions a popover
          whose anchor is 0×0 and fixed (no offsetParent), so it stays hidden. */}
      <span
        aria-hidden
        style={{
          position: "fixed",
          top: anchor.top,
          left: anchor.left,
          width: 1,
          height: 1,
          pointerEvents: "none",
        }}
      />
    </Popover>
  );
};

export default StandoutCreatePopover;
